import {
  ProductCategoryAutocompleteDocument,
  type ProductCategoryAutocompleteQuery,
  type ProductCategoryAutocompleteQueryVariables,
} from "@/generated/graphql";
import type { AutocompleteFetchParams, AutocompleteFetchResult } from "@k3-tech/react-kit";
import { useCallback } from "react";
import { useClient } from "urql";

export const useProductCategoryAutocomplete = () => {
  const urqlClient = useClient();

  const load = useCallback(
    async (page: number, pageSize: number, search: string, moreFilter?: AutocompleteFetchParams['moreFilter']) => {
      const { data } = await urqlClient
        .query<
          ProductCategoryAutocompleteQuery,
          ProductCategoryAutocompleteQueryVariables
        >(
          ProductCategoryAutocompleteDocument,
          {
            qop: {
              pagination: {
                page,
                pageSize,
                withCount: true,
              },
              filters: { productCategoryName: search, ...(moreFilter?.() || {}) },
            },
          },
          { suspense: false, requestPolicy: "network-only" },
        )
        .toPromise();

      const rawItems = (data?.productCategories?.items ?? []) as NonNullable<
        ProductCategoryAutocompleteQuery["productCategories"]
      >["items"];
      const items = (rawItems || [])
        .filter((it): it is NonNullable<typeof it> => Boolean(it))
        .map((item) => ({ value: item.productCategoryID, label: item.productCategoryName, raw: item }));

      return {
        items: items ?? [],
        total: data?.productCategories?.pagination?.totalRows ?? 0,
        hasMore: data?.productCategories?.pagination?.hasNext ?? false,
      } as AutocompleteFetchResult;
    },
    [urqlClient],
  );

  return { load };
};
